import {
  CANNONS,
  CARS,
  DEATH_EXPLOSIONS,
  ENGINE_SOUNDS,
  NITRO_EFFECTS,
  PROJECTILES,
  TOMBSTONES,
  WHEELS,
  STANDARD_CAR_PERFORMANCE,
} from './data.js';
import { registerImportedVehicleDefinition } from './carFactory.js';
import { getSupabaseClient, isSupabaseConfigured } from '../services/supabaseClient.js';
import { DEFAULT_IMPORTED_VEHICLE_VISUAL_SCALE } from '../tools/vehicleProfile.js';

const CACHE_KEY='carscape-published-catalog-v1';
const LOAD_TIMEOUT_MS=4000;
const REQUIRED_CATALOGS=Object.freeze({
  vehicle:'autos',
  wheel:'ruedas',
  engineSound:'sonidos de motor',
  cannon:'cañones',
  projectile:'proyectiles',
  nitro:'nitros',
  explosion:'explosiones',
  tombstone:'lápidas',
});

export const catalogRuntimeStatus={
  source:'local',
  releaseId:null,
  releaseNumber:null,
  itemCount:0,
  error:null,
  authoritative:false,
  blocked:false,
};

function upsertCatalogItem(catalog,item){
  const index=catalog.findIndex((candidate)=>candidate.id===item.id);
  if(index>=0)catalog.splice(index,1,item);
  else catalog.push(item);
}

function designFromEntry(entry){
  return entry.profile?.design&&typeof entry.profile.design==='object'
    ?entry.profile.design
    :{};
}

function thumbnailFromEntry(entry){
  return entry.assets?.thumbnail?.url||null;
}

function economyFromEntry(entry){
  const starter=entry.starter===true;
  const price=Math.max(1,Math.round(Number(entry.economy?.price)||250));
  return {
    starter,
    purchasable:!starter&&entry.economy?.purchasable!==false,
    priceCrowns:price,
    currency:'crowns',
  };
}

function vehiclePalette(entry){
  const palettes={
    common:['#e86a24','#f3eee2'],
    rare:['#3977ee','#5be8ff'],
    epic:['#8b65df','#ff77c8'],
    legendary:['#efb82f','#fff0a5'],
  };
  return palettes[entry.rarity]||palettes.common;
}

function installVehicle(entry,catalogs){
  const bodyUrl=entry.assets?.body?.url;
  if(!bodyUrl||!entry.profile)return false;
  const [color,accent]=vehiclePalette(entry);
  const game=entry.profile.game||{};
  const defaultCannonId=game.defaultCannonId||catalogs.cannon[0].id;
  const defaultCannon=catalogs.cannon.find((item)=>item.id===defaultCannonId)||catalogs.cannon[0];
  registerImportedVehicleDefinition(entry.id,{
    profile:entry.profile,
    bodyUrl,
    visualScale:Number(game.visualScale)||DEFAULT_IMPORTED_VEHICLE_VISUAL_SCALE,
  });
  upsertCatalogItem(catalogs.vehicle,{
    id:entry.id,
    name:entry.name||entry.id,
    className:game.className||'Importado',
    description:entry.description||'Carrocería publicada desde Catalog Studio.',
    color:game.color||color,
    accent:game.accent||accent,
    importedModelId:entry.id,
    bodyStyle:game.bodyStyle||'muscle',
    ...economyFromEntry(entry),
    defaultWheelId:game.defaultWheelId||catalogs.wheel[0].id,
    defaultEngineSoundId:game.defaultEngineSoundId||catalogs.engineSound[0].id,
    defaultCannonId,
    defaultProjectileId:game.defaultProjectileId||defaultCannon.defaultProjectileId||catalogs.projectile[0].id,
    defaultNitroId:game.defaultNitroId||catalogs.nitro[0].id,
    defaultExplosionId:game.defaultExplosionId||catalogs.explosion[0].id,
    defaultTombstoneId:game.defaultTombstoneId||catalogs.tombstone[0].id,
    thumbnailUrl:thumbnailFromEntry(entry),
    catalogVersion:entry.version,
    ...STANDARD_CAR_PERFORMANCE,
  });
  return true;
}

function installWheel(entry,catalog){
  const mode=entry.profile?.mode==='glb'?'glb':'procedural';
  const assetUrl=entry.assets?.asset?.url||null;
  if(mode==='glb'&&!assetUrl)return false;
  const design=designFromEntry(entry);
  upsertCatalogItem(catalog,{
    rimStyle:'spokes',
    tireColor:'#242532',
    tireWidth:.44,
    spokes:6,
    spokeWidth:.3,
    spokeHeight:.12,
    spokeLength:.58,
    holeCount:8,
    holeSize:.085,
    holeOrbit:.38,
    steelDish:.1,
    steelWave:.035,
    inner:.39,
    rim:.55,
    rimThickness:.055,
    rimOffset:-.12,
    hubEnabled:true,
    hub:.28,
    hubDepth:1.12,
    accentRingEnabled:true,
    accentRingRadius:.72,
    accentRingThickness:.026,
    centerCapEnabled:true,
    centerCapSize:.14,
    centerCapRelief:.36,
    tireRoundness:.34,
    tread:0,
    metalness:.72,
    roughness:.3,
    ...design,
    mode,
    assetUrl,
    id:entry.id,
    name:entry.name||entry.id,
    description:entry.description||'Rueda publicada desde Catalog Studio.',
    color:design.color||'#d9dde2',
    accent:design.accent||'#f5f7fa',
    thumbnailUrl:thumbnailFromEntry(entry),
    rimOffset:Number.isFinite(Number(design.rimOffset))?Number(design.rimOffset):-.12,
    ...economyFromEntry(entry),
    catalogVersion:entry.version,
  });
  return true;
}

function installDesignItem(catalog,entry,fallback){
  const design=designFromEntry(entry);
  upsertCatalogItem(catalog,{
    ...fallback,
    ...design,
    mode:entry.profile?.mode||'procedural',
    id:entry.id,
    name:entry.name||entry.id,
    description:entry.description||fallback.description,
    ...economyFromEntry(entry),
    thumbnailUrl:thumbnailFromEntry(entry),
    catalogVersion:entry.version,
  });
  return true;
}

export function applyPublishedCatalog(manifest){
  if(manifest?.schema!=='toybox-catalog-release@1'||!Array.isArray(manifest.items))return 0;
  const catalogs={
    vehicle:[],
    wheel:[],
    cannon:[],
    engineSound:[],
    projectile:[],
    nitro:[],
    explosion:[],
    tombstone:[],
  };
  let installed=0;

  // Primero se instalan los cosméticos, porque los autos necesitan defaults
  // válidos al construir su loadout inicial.
  for(const entry of manifest.items){
    if(!entry?.id||!entry.type)continue;
    if(entry.type==='wheel')installed+=Number(installWheel(entry,catalogs.wheel));
    if(entry.type==='engine_sound')installed+=Number(installDesignItem(catalogs.engineSound,entry,{
      description:'Sonido de motor publicado desde Catalog Studio.',
      wave:'sawtooth',
      baseTone:58,
      speedPitch:7,
      boostPitch:45,
      filterType:'lowpass',
      filter:620,
      resonance:1,
      volume:.035,
      pulse:6,
      noiseGain:.005,
      sampleIdleRate:1,
      sampleSpeedRate:.04,
      sampleBoostRate:.35,
      sampleVolume:.28,
      sampleFilter:12000,
      sampleLoopStart:0,
      sampleLoopEndTrim:0,
      sampleLoopCrossfade:.04,
      assetUrl:entry.assets?.asset?.url||null,
    }));
    if(entry.type==='projectile')installed+=Number(installDesignItem(catalogs.projectile,entry,{
      description:'Proyectil publicado desde Catalog Studio.',
      style:'dart',
      color:'#d9dde2',
      accent:'#ffcf43',
      scale:1,
      spin:8,
      travelSoundMode:entry.profile?.sounds?.travel?.mode==='mp3'?'mp3':'procedural',
      travelSoundUrl:entry.assets?.['audio-travel']?.url||null,
      travelSampleVolume:.5,
      travelSampleRate:1,
      impactSoundMode:entry.profile?.sounds?.impact?.mode==='mp3'?'mp3':'procedural',
      impactSoundUrl:entry.assets?.['audio-impact']?.url||null,
      impactSampleVolume:.5,
      impactSampleRate:1,
      assetUrl:entry.assets?.asset?.url||null,
    }));
    if(entry.type==='cannon')installed+=Number(installDesignItem(catalogs.cannon,entry,{
      description:'Cañón publicado desde Catalog Studio.',
      color:'#d9dde2',
      accent:'#ffcf43',
      style:'single',
      soundMode:entry.profile?.sound?.mode==='mp3'?'mp3':'procedural',
      soundSampleVolume:.5,
      soundSampleRate:1,
      soundUrl:entry.assets?.audio?.url||null,
      assetUrl:entry.assets?.asset?.url||null,
    }));
    if(entry.type==='nitro')installed+=Number(installDesignItem(catalogs.nitro,entry,{
      description:'Nitro publicado desde Catalog Studio.',
      color:'#5ef3ff',
      accent:'#ffef75',
      spark:'#66eaff',
      style:'flame',
      soundMode:entry.profile?.sound?.mode==='mp3'?'mp3':'procedural',
      soundUrl:entry.assets?.audio?.url||null,
      soundSampleVolume:.5,
      soundSampleRate:1,
      sampleLoopStart:0,
      sampleLoopEndTrim:0,
      sampleLoopCrossfade:.04,
    }));
    if(entry.type==='explosion')installed+=Number(installDesignItem(catalogs.explosion,entry,{
      description:'Explosión publicada desde Catalog Studio.',
      color:'#ffd83d',
      accent:'#ff742f',
      style:'burst',
      soundMode:entry.profile?.sound?.mode==='mp3'?'mp3':'procedural',
      soundUrl:entry.assets?.audio?.url||null,
      soundSampleVolume:.5,
      soundSampleRate:1,
    }));
    if(entry.type==='tombstone'){
      installed+=Number(installDesignItem(catalogs.tombstone,entry,{
        description:'Lápida publicada desde Catalog Studio.',
        color:'#6f7180',
        accent:'#d9dde2',
        style:'stone',
        scale:1,
        soundMode:entry.profile?.sound?.mode==='mp3'?'mp3':'procedural',
        soundUrl:entry.assets?.audio?.url||null,
        soundSampleVolume:.5,
        soundSampleRate:1,
        assetUrl:entry.assets?.asset?.url||null,
      }));
    }
  }

  const missingCosmetics=Object.entries(REQUIRED_CATALOGS)
    .filter(([type])=>type!=='vehicle'&&!catalogs[type].length)
    .map(([,label])=>label);
  if(missingCosmetics.length){
    throw new Error(`El release publicado está incompleto. Faltan: ${missingCosmetics.join(', ')}.`);
  }
  for(const entry of manifest.items){
    if(entry?.type==='vehicle')installed+=Number(installVehicle(entry,catalogs));
  }
  if(!catalogs.vehicle.length){
    throw new Error('El release publicado está incompleto. Falta al menos un auto GLB.');
  }

  CARS.splice(0,CARS.length,...catalogs.vehicle);
  WHEELS.splice(0,WHEELS.length,...catalogs.wheel);
  CANNONS.splice(0,CANNONS.length,...catalogs.cannon);
  ENGINE_SOUNDS.splice(0,ENGINE_SOUNDS.length,...catalogs.engineSound);
  PROJECTILES.splice(0,PROJECTILES.length,...catalogs.projectile);
  NITRO_EFFECTS.splice(0,NITRO_EFFECTS.length,...catalogs.nitro);
  DEATH_EXPLOSIONS.splice(0,DEATH_EXPLOSIONS.length,...catalogs.explosion);
  TOMBSTONES.splice(0,TOMBSTONES.length,...catalogs.tombstone);
  catalogRuntimeStatus.itemCount=installed;
  catalogRuntimeStatus.authoritative=true;
  catalogRuntimeStatus.blocked=false;
  return installed;
}

function readCache(){
  try{return JSON.parse(globalThis.localStorage?.getItem(CACHE_KEY)||'null');}
  catch{return null;}
}

function writeCache(release){
  try{globalThis.localStorage?.setItem(CACHE_KEY,JSON.stringify(release));}
  catch{/* El juego puede funcionar sin caché persistente. */}
}

function timeout(ms){
  return new Promise((_,reject)=>{
    setTimeout(()=>reject(new Error('La carga del catálogo remoto excedió el tiempo límite.')),ms);
  });
}

export async function loadPublishedCatalog(){
  const cached=readCache();
  if(!isSupabaseConfigured()){
    if(cached?.manifest){
      try{
        applyPublishedCatalog(cached.manifest);
        catalogRuntimeStatus.source='cache';
        catalogRuntimeStatus.releaseId=cached.id||cached.manifest.releaseId||null;
        catalogRuntimeStatus.releaseNumber=cached.release_number||null;
      }catch(error){
        catalogRuntimeStatus.error=error.message||String(error);
      }
    }
    return catalogRuntimeStatus;
  }
  try{
    const client=getSupabaseClient();
    const request=client
      .from('catalog_releases')
      .select('id,release_number,manifest,published_at')
      .order('release_number',{ascending:false})
      .limit(1)
      .maybeSingle();
    const {data,error}=await Promise.race([request,timeout(LOAD_TIMEOUT_MS)]);
    if(error)throw error;
    if(data?.manifest){
      applyPublishedCatalog(data.manifest);
      writeCache(data);
      catalogRuntimeStatus.source='supabase';
      catalogRuntimeStatus.releaseId=data.id;
      catalogRuntimeStatus.releaseNumber=data.release_number;
      return catalogRuntimeStatus;
    }
  }catch(error){
    catalogRuntimeStatus.error=error.message||String(error);
    console.warn('No se pudo instalar el último catálogo publicado.',error);
  }
  if(cached?.manifest){
    try{
      applyPublishedCatalog(cached.manifest);
      catalogRuntimeStatus.source='cache';
      catalogRuntimeStatus.releaseId=cached.id||cached.manifest.releaseId||null;
      catalogRuntimeStatus.releaseNumber=cached.release_number||null;
      return catalogRuntimeStatus;
    }catch(error){
      catalogRuntimeStatus.error=error.message||String(error);
    }
  }
  catalogRuntimeStatus.blocked=true;
  catalogRuntimeStatus.source='unavailable';
  catalogRuntimeStatus.error||='Todavía no existe un release completo publicado desde Catalog Studio.';
  return catalogRuntimeStatus;
}
